/**
 * F-partner-account (M2) — Conta-corrente do sócio (compte courant d'associé).
 *
 * Saldo corrente = Σ aberturas + Σ(transações partner_contribution +
 * partner_reimbursement + personal, somadas com sinal). Positivo = a empresa
 * DEVE ao sócio; negativo = o sócio deve à empresa.
 */

import { useState } from "react";
import { BankCard, BankShell, bankInputClass } from "../components/bank/Shell.js";
import { useModals } from "../components/common/ModalsProvider.js";
import { useAuth } from "../hooks/useAuth.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { useT } from "../i18n/index.js";
import { formatMoney } from "../lib/format-fr.js";
import { trpc } from "../lib/trpc.js";
import { FILTER_INPUT } from "../components/common/FilterBar.js";

export default function BankSocio() {
  const { user } = useAuth();
  const canEdit = useCanEdit();
  const modals = useModals();
  const t = useT();
  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );
  const [companyId, setCompanyId] = useSelectedCompany(accessibleCompanies);

  const utils = trpc.useUtils();
  const summaryQuery = trpc.partnerAccounts.summary.useQuery(
    { companyId: companyId ?? 0 },
    { enabled: companyId !== null },
  );

  const isAdmin =
    user?.isSuperAdmin === true ||
    (user?.memberships ?? []).some(
      (m) => m.companyId === companyId && m.active && m.role === "admin",
    );

  const createMut = trpc.partnerAccounts.create.useMutation({
    onSuccess: () => utils.partnerAccounts.invalidate(),
    onError: (e) =>
      void modals.alert({ title: t("bankPartner.error"), message: e.message, tone: "danger" }),
  });
  const updateMut = trpc.partnerAccounts.update.useMutation({
    onSuccess: () => utils.partnerAccounts.invalidate(),
    onError: (e) =>
      void modals.alert({ title: t("bankPartner.error"), message: e.message, tone: "danger" }),
  });
  const deleteMut = trpc.partnerAccounts.delete.useMutation({
    onSuccess: () => utils.partnerAccounts.invalidate(),
    onError: (e) =>
      void modals.alert({ title: t("bankPartner.error"), message: e.message, tone: "danger" }),
  });

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [opening, setOpening] = useState("0");
  const [openingDate, setOpeningDate] = useState("");

  if (!user) return null;
  if (accessibleCompanies.length === 0) {
    return (
      <BankShell subtitle={t("bankPartner.subtitle")}>
        <p className="text-sm text-neutral-400">{t("bankPartner.noCompanyAccess")}</p>
      </BankShell>
    );
  }

  const s = summaryQuery.data;
  const balance = s?.currentBalance ?? 0;
  const balanceTone = balance > 0 ? "text-lime" : balance < 0 ? "text-red-400" : "text-neutral-300";

  function submitCreate() {
    if (companyId === null) return;
    if (!name.trim()) {
      void modals.alert({
        title: t("bankPartner.missingDataTitle"),
        message: t("bankPartner.missingPartnerName"),
        tone: "danger",
      });
      return;
    }
    const normalized = opening.replace(",", ".").trim() || "0";
    createMut.mutate(
      {
        companyId,
        partnerName: name.trim(),
        openingBalance: normalized,
        openingBalanceDate: openingDate || null,
      },
      {
        onSuccess: () => {
          setShowCreate(false);
          setName("");
          setOpening("0");
          setOpeningDate("");
        },
      },
    );
  }

  const toolbar = (
    <div className="flex items-center gap-3 flex-wrap">
      <label className="text-sm text-neutral-400">{t("bankPartner.companyLabel")}:</label>
      <select
        value={companyId ?? ""}
        onChange={(e) => setCompanyId(Number(e.target.value))}
        className={FILTER_INPUT + " w-56"}
      >
        {accessibleCompanies.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <BankShell subtitle={t("bankPartner.subtitle")} toolbar={toolbar}>
      {summaryQuery.isLoading && (
        <p className="text-sm text-neutral-500">{t("bankPartner.loading")}</p>
      )}
      {summaryQuery.error && <p className="text-sm text-red-400">{summaryQuery.error.message}</p>}

      {s && (
        <>
          {/* Saldo corrente + quebra */}
          <BankCard
            title={t("bankPartner.accountCardTitle")}
            subtitle={t("bankPartner.accountCardSubtitle")}
          >
            <div className="flex flex-col gap-1">
              <span className="text-xs uppercase tracking-wider text-neutral-500">
                {t("bankPartner.currentBalance")}
              </span>
              <span className={`text-3xl font-mono font-bold ${balanceTone}`}>
                {formatMoney(balance, { decimals: 2 })}
              </span>
              <span className="text-xs text-neutral-500">
                {balance > 0
                  ? t("bankPartner.companyOwesPartner")
                  : balance < 0
                    ? t("bankPartner.partnerOwesCompany")
                    : t("bankPartner.balanceZero")}
              </span>
            </div>

            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Kpi label={t("bankPartner.opening")} value={s.openingTotal} tone="neutral" />
              <Kpi
                label={`${t("bankPartner.contributions")} (${s.contributions.count})`}
                value={s.contributions.total}
                tone="lime"
              />
              <Kpi
                label={`${t("bankPartner.reimbursements")} (${s.reimbursements.count})`}
                value={s.reimbursements.total}
                tone="red"
              />
              <Kpi
                label={`${t("bankPartner.personalExpenses")} (${s.personal.count})`}
                value={s.personal.total}
                tone="red"
              />
            </div>

            <p className="mt-4 text-[11px] text-neutral-600 leading-relaxed">
              {t("bankPartner.calcOpeningPre")} {formatMoney(s.openingTotal, { decimals: 2 })} +{" "}
              {t("bankPartner.calcMovements")} {formatMoney(s.transactionsDelta, { decimals: 2 })} ={" "}
              {formatMoney(balance, { decimals: 2 })}.{" "}
              {s.afterDate
                ? `${t("bankPartner.movementsFromPre")} ${s.afterDate} ${t("bankPartner.movementsFromSuf")}`
                : t("bankPartner.allMovementsCounted")}{" "}
              {t("bankPartner.movementsSourceNote")}
              {s.partnerCount > 1 && ` ⚠️ ${t("bankPartner.multiPartnerWarning")}`}
            </p>
          </BankCard>

          {/* Aberturas (CRUD) */}
          <BankCard
            title={t("bankPartner.openingsCardTitle")}
            action={
              canEdit ? (
                <button
                  onClick={() => setShowCreate((v) => !v)}
                  className="rounded-md border border-lime/40 hover:border-lime text-lime px-3 py-1 text-xs"
                >
                  {showCreate ? t("bankPartner.cancel") : t("bankPartner.newPartnerBtn")}
                </button>
              ) : undefined
            }
          >
            {showCreate && (
              <div className="mb-4 rounded-md border border-lime/20 bg-lime/[0.03] p-3 grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
                <Labeled label={t("bankPartner.partnerNameLabel")}>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("bankPartner.partnerNamePlaceholder")}
                    className={bankInputClass + " w-full"}
                  />
                </Labeled>
                <Labeled label={t("bankPartner.openingBalanceLabel")}>
                  <input
                    value={opening}
                    onChange={(e) => setOpening(e.target.value)}
                    inputMode="decimal"
                    placeholder="0,00"
                    className={bankInputClass + " w-full font-mono text-right"}
                  />
                </Labeled>
                <Labeled label={t("bankPartner.openingDateLabel")}>
                  <input
                    type="date"
                    value={openingDate}
                    onChange={(e) => setOpeningDate(e.target.value)}
                    className={bankInputClass + " w-full"}
                  />
                </Labeled>
                <button
                  onClick={submitCreate}
                  disabled={createMut.isPending}
                  className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-4 py-1.5 text-sm font-medium"
                >
                  {createMut.isPending ? t("bankPartner.saving") : t("bankPartner.save")}
                </button>
              </div>
            )}

            {s.accounts.length === 0 ? (
              <p className="text-sm text-neutral-600 italic">{t("bankPartner.emptyOpenings")}</p>
            ) : (
              <div className="space-y-2">
                {s.accounts.map((a) => (
                  <PartnerRow
                    key={a.id}
                    account={a}
                    canEdit={canEdit}
                    isAdmin={isAdmin}
                    busy={updateMut.isPending || deleteMut.isPending}
                    onSave={(patch) =>
                      updateMut.mutate({ companyId: companyId!, id: a.id, ...patch })
                    }
                    onDelete={async () => {
                      const ok = await modals.confirm({
                        title: t("bankPartner.removePartnerTitle"),
                        message: `${t("bankPartner.removeConfirmPre")} "${a.partnerName}"? ${t("bankPartner.removeConfirmSuf")}`,
                        confirmLabel: t("bankPartner.remove"),
                        tone: "danger",
                      });
                      if (ok) deleteMut.mutate({ companyId: companyId!, id: a.id });
                    }}
                  />
                ))}
              </div>
            )}
          </BankCard>
        </>
      )}
    </BankShell>
  );
}

function Kpi({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "lime" | "red" | "neutral";
}) {
  const color =
    tone === "lime" ? "text-lime" : tone === "red" ? "text-red-400" : "text-neutral-200";
  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-900/40 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-neutral-500">{label}</div>
      <div className={`font-mono text-sm font-semibold ${color}`}>
        {formatMoney(value, { decimals: 2 })}
      </div>
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[11px] text-neutral-400 mb-1">{label}</label>
      {children}
    </div>
  );
}

type Account = {
  id: number;
  partnerName: string;
  openingBalance: string;
  openingBalanceDate: string | null;
  notes: string | null;
};

function PartnerRow({
  account,
  canEdit,
  isAdmin,
  busy,
  onSave,
  onDelete,
}: {
  account: Account;
  canEdit: boolean;
  isAdmin: boolean;
  busy: boolean;
  onSave: (patch: {
    partnerName?: string;
    openingBalance?: string;
    openingBalanceDate?: string | null;
  }) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(account.partnerName);
  const [opening, setOpening] = useState(String(account.openingBalance));
  const [date, setDate] = useState(account.openingBalanceDate ?? "");
  const t = useT();

  if (!editing) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-neutral-800 px-3 py-2">
        <div>
          <div className="text-sm text-neutral-200">{account.partnerName}</div>
          <div className="text-xs text-neutral-500">
            {t("bankPartner.opening")} {formatMoney(account.openingBalance, { decimals: 2 })}
            {account.openingBalanceDate ? ` · ${account.openingBalanceDate}` : ""}
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setEditing(true)}
              className="rounded-md border border-neutral-800 hover:border-sky-500 text-sky-300 px-2 py-0.5 text-xs"
            >
              {t("bankPartner.editBtn")}
            </button>
            {isAdmin && (
              <button
                onClick={onDelete}
                disabled={busy}
                className="rounded-md border border-red-500/40 hover:border-red-500 text-red-300 px-2 py-0.5 text-xs disabled:opacity-40"
              >
                {t("bankPartner.removeBtn")}
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-md border border-sky-500/30 bg-sky-500/[0.03] px-3 py-2 grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
      <Labeled label={t("bankPartner.nameLabel")}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={bankInputClass + " w-full"}
        />
      </Labeled>
      <Labeled label={t("bankPartner.openingEuroLabel")}>
        <input
          value={opening}
          onChange={(e) => setOpening(e.target.value)}
          inputMode="decimal"
          className={bankInputClass + " w-full font-mono text-right"}
        />
      </Labeled>
      <Labeled label={t("bankPartner.openingDateLabel")}>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className={bankInputClass + " w-full"}
        />
      </Labeled>
      <div className="flex gap-2">
        <button
          onClick={() => {
            onSave({
              partnerName: name.trim(),
              openingBalance: opening.replace(",", ".").trim() || "0",
              openingBalanceDate: date || null,
            });
            setEditing(false);
          }}
          disabled={busy}
          className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-50 px-3 py-1.5 text-sm font-medium"
        >
          {t("bankPartner.saveBtnLower")}
        </button>
        <button
          onClick={() => setEditing(false)}
          className="rounded-md border border-neutral-800 hover:border-neutral-700 px-3 py-1.5 text-sm"
        >
          {t("bankPartner.cancelBtnLower")}
        </button>
      </div>
    </div>
  );
}
